import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SupportedLanguage } from '@demind-inc/core';
import { useSetRecoilState } from 'recoil';

import { changeI18nLanguage, getCurrentI18nLanguage, translateI18n } from '../../i18n';
import {
  LANGUAGE_OPTIONS,
  isSupportedLanguage,
  resolveAppLanguage,
  resolveSupportedLanguage,
} from '../../i18n/languages';
import { useUpdateGeneralPref } from '../mutations';
import { usePreferences } from '../queries';
import { generalSnackBarAtom } from '../recoil';
import { Status } from '../types';
import { useAuthContext } from './AuthProvider';

interface LanguageContextValue {
  language: SupportedLanguage;
  languageOptions: typeof LANGUAGE_OPTIONS;
  changeLanguage: (nextLanguage: SupportedLanguage) => Promise<void>;
  isLanguageLoading: boolean;
  isLanguageSaving: boolean;
}

export const LanguageContext = createContext({} as LanguageContextValue);

export const useLanguageContext = () => useContext(LanguageContext);

type FreshPreferenceResult = {
  preferenceId: string;
  status: 'success' | 'error';
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { status, user } = useAuthContext();
  const preferenceId = user.preferenceId;
  const [language, setLanguage] = useState<SupportedLanguage>(() =>
    resolveSupportedLanguage(getCurrentI18nLanguage())
  );
  const [freshPreferenceResult, setFreshPreferenceResult] = useState<FreshPreferenceResult | null>(
    null
  );
  const [isFreshPreferenceLoading, setIsFreshPreferenceLoading] = useState(false);
  const setGeneralSnackbar = useSetRecoilState(generalSnackBarAtom);

  const { generalPreferences, refetch } = usePreferences({ preferenceId: preferenceId ?? '' });
  const { updateGeneralPref, isPending: isLanguageSaving } = useUpdateGeneralPref();

  useEffect(() => {
    if (!preferenceId) {
      setFreshPreferenceResult(null);
      setIsFreshPreferenceLoading(false);
      return;
    }

    let isCancelled = false;

    setFreshPreferenceResult(null);
    setIsFreshPreferenceLoading(true);

    refetch()
      .then((result) => {
        if (!isCancelled && result.isSuccess) {
          setFreshPreferenceResult({ preferenceId, status: 'success' });
          return;
        }

        if (!isCancelled) {
          setFreshPreferenceResult({ preferenceId, status: 'error' });
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setFreshPreferenceResult({ preferenceId, status: 'error' });
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsFreshPreferenceLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [preferenceId, refetch]);

  const resolvedLanguage = useMemo(() => {
    const shouldWaitForUserPreferenceId =
      status === Status.LOADING || (status === Status.AUTHORIZED && !preferenceId);

    if (shouldWaitForUserPreferenceId) {
      return resolveSupportedLanguage(getCurrentI18nLanguage());
    }

    const hasCompletedFreshPreferenceAttempt = Boolean(
      !preferenceId || freshPreferenceResult?.preferenceId === preferenceId
    );

    if (!hasCompletedFreshPreferenceAttempt) {
      return resolveSupportedLanguage(getCurrentI18nLanguage());
    }

    const hasFreshPreferences =
      preferenceId &&
      freshPreferenceResult?.preferenceId === preferenceId &&
      freshPreferenceResult.status === 'success';
    const preferredLanguage = generalPreferences?.preferredLanguage as unknown;

    if (
      preferenceId &&
      freshPreferenceResult?.preferenceId === preferenceId &&
      freshPreferenceResult.status === 'error'
    ) {
      if (isSupportedLanguage(preferredLanguage)) {
        return preferredLanguage;
      }

      return resolveSupportedLanguage(getCurrentI18nLanguage());
    }

    return resolveAppLanguage({
      preferredLanguage: hasFreshPreferences ? preferredLanguage : undefined,
    });
  }, [freshPreferenceResult, generalPreferences?.preferredLanguage, preferenceId, status]);

  useEffect(() => {
    setLanguage(resolvedLanguage);

    if (getCurrentI18nLanguage() !== resolvedLanguage) {
      changeI18nLanguage(resolvedLanguage);
    }
  }, [resolvedLanguage]);

  const changeLanguage = useCallback(
    async (nextLanguage: SupportedLanguage) => {
      if (!preferenceId || !isSupportedLanguage(nextLanguage) || nextLanguage === language) {
        return;
      }

      const previousLanguage = language;

      setLanguage(nextLanguage);
      await changeI18nLanguage(nextLanguage);

      try {
        await updateGeneralPref({
          preferenceId,
          preferences: { preferredLanguage: nextLanguage },
        });
      } catch (error) {
        setLanguage(previousLanguage);
        await changeI18nLanguage(previousLanguage);
        setGeneralSnackbar(translateI18n('settings.general.languageSaveError'));
      }
    },
    [language, preferenceId, setGeneralSnackbar, updateGeneralPref]
  );

  const value = useMemo(
    () => ({
      language,
      languageOptions: LANGUAGE_OPTIONS,
      changeLanguage,
      isLanguageLoading: isFreshPreferenceLoading,
      isLanguageSaving,
    }),
    [changeLanguage, isFreshPreferenceLoading, isLanguageSaving, language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
